import { decodeDisplayText } from '../../../../util/displayText';
import '../../../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import ReactPlayer from 'react-player';
import { usePopper } from 'react-popper';
import Duration from '../../../Duration';
import { IoIosPause } from 'react-icons/io';
import { FaPlay } from 'react-icons/fa';
import { FiArrowRight, FiClock, FiFileText, FiX } from 'react-icons/fi';
import { LuScanText } from 'react-icons/lu';

import '../../../AudioControls.scss';
import './AudioMessage.scss';
import { FILE_BLUE, WHITE } from '../../../../appearance/Colors';
import { useMutation, useQuery } from '@apollo/client';
import { MUTATION, QUERY } from '../../../../gql-operations';
import { GET_FEATURE_FLAG } from '../../../../gql-operations/featureFlags';
import CreditsRequiredModal from '../../../Modals/CreditsRequiredModal';
import useCredits from '../../../../hooks/useCredits';
import { openSettingsModal, SETTINGS_MODAL_TAB } from '../../../../util/settingsModalEvents';
import {
    FEATURE_UPGRADE_MODAL_PRESET,
    openFeatureUpgradeModal
} from '../../../../util/featureUpgradeModalEvents';
import {
    evaluateFeatureFlagForUser,
    getFeatureFlagUpgradeMessage,
    mapTranscriptionErrorToUiAction
} from '../../../../util/featureFlagAccess';
import { normalizeDurationToSeconds, resolvePreferredMediaDuration } from '../../../../util/mediaDuration';
import {
    AUDIO_TRANSCRIPTION_OUTPUT_FORMAT,
    AUDIO_TRANSCRIPTION_VISIBLE_OPTIONS
} from '../../../../util/audioTranscriptionOptions';
import {
    getApolloErrorMessage,
    getAudioProcessingStatusText,
    getAudioProcessingStatusTone,
    isAudioProcessingActive
} from '../../../../util/audioProcessingStatus';
import { setOpenMenuMessage, getOpenMenuMessage, addMenuListener } from '../../DefaultMessage';
import {
    DropDownMenu,
    Element,
    MenuItemLabel,
    MenuItemIcon
} from '../../MessageContextMenu';
import Modal from '../../../Modals/Modal';

const PLAYBACK_SPEEDS = [1, 1.5, 2, 2.5];
const TRANSCRIPTION_INTRO_DISMISS_KEY = 'luckynote_hide_audio_transcription_intro';

const formatPlaybackRate = value => `${value}x`;

const getShouldHideTranscriptionIntro = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    try {
        return window.localStorage.getItem(TRANSCRIPTION_INTRO_DISMISS_KEY) === 'true';
    } catch {
        return false;
    }
};

function AudioMessage({
    url,
    fallbackUrl,
    title,
    showTitle = true,
    fileName,
    fileSize,
    initialDuration,
    message,
    showTranscriptionIntro = false,
    isUploading = false,
    uploadProgress = 0,
    uploadStatus = 'uploading',
    onCancelUpload
}) {
    const {t: lnT} = useTranslation();

    const playerRef = useRef(null);
    const isResettingAfterEndRef = useRef(false);
    const durationSyncTimeoutRef = useRef(null);

    // Transcription state
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [transcriptionError, setTranscriptionError] = useState(null);
    const [transcriptionStatus, setTranscriptionStatus] = useState(null);
    const [showCreditsRequiredModal, setShowCreditsRequiredModal] = useState(false);
    const [showTranscriptionOutputMenu, setShowTranscriptionOutputMenu] = useState(false);
    const [showTranscriptionIntroModal, setShowTranscriptionIntroModal] = useState(false);
    const [hideTranscriptionIntro, setHideTranscriptionIntro] = useState(false);
    const [pendingOutputFormat, setPendingOutputFormat] = useState(null);
    const [playbackUrl, setPlaybackUrl] = useState(url);
    const [isRefreshingPlaybackUrl, setIsRefreshingPlaybackUrl] = useState(false);
    const hasRetriedPlaybackUrlRef = useRef(false);
    const [transcriptionMenuReferenceElement, setTranscriptionMenuReferenceElement] = useState(null);
    const [transcriptionMenuPopperElement, setTranscriptionMenuPopperElement] = useState(null);
    const transcriptionMenuKey = React.useMemo(
        () => `audio-message-transcribe:${message?.id || fileName || url}`,
        [lnT, fileName, message?.id, url]
    );
    const audioProcessing = message?.metadata?.audioProcessing;
    const serverTranscriptionStatus = getAudioProcessingStatusText(audioProcessing);
    const transcriptionStatusTone = getAudioProcessingStatusTone(audioProcessing);
    const { styles: transcriptionMenuPopperStyles, attributes: transcriptionMenuPopperAttributes } = usePopper(
        transcriptionMenuReferenceElement,
        transcriptionMenuPopperElement,
        {
            strategy: 'fixed',
            placement: 'bottom-end',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 6]
                    }
                }
            ]
        }
    );
    
    // Get user data for feature flag evaluation
    const { data: userData } = useQuery(QUERY.ME, {
        errorPolicy: 'all'
    });
    const { refetch: refetchDownloadFile } = useQuery(QUERY.DOWNLOAD_FILE, { skip: true });
    
    // Feature flag check for audio transcription
    const { data: transcriptionFlagData, loading: transcriptionFlagLoading } = useQuery(GET_FEATURE_FLAG, {
        variables: { name: 'audio_transcription' },
        errorPolicy: 'all'
    });
    
    const transcriptionFlag = transcriptionFlagData?.getFeatureFlag;
    const transcriptionAccessDecision = React.useMemo(
        () => evaluateFeatureFlagForUser(transcriptionFlag, userData?.me, { loading: transcriptionFlagLoading }),
        [lnT, transcriptionFlag, userData?.me, transcriptionFlagLoading]
    );
    const isTranscriptionFeatureEnabled = transcriptionAccessDecision?.allowed;
    const showTranscriptionAction = !!transcriptionFlag?.enabled;
    const requiresUpgradeForTranscription = Boolean(
        showTranscriptionAction && transcriptionAccessDecision?.requiresUpgrade
    );
    const requiresTeamUpgradeForTranscription = Boolean(
        showTranscriptionAction && transcriptionAccessDecision?.requiresTeamUpgrade
    );
    const upgradeTooltip = getFeatureFlagUpgradeMessage(transcriptionFlag, 'audio transcription');
    const { credits } = useCredits({
        skip: !userData?.me?.id,
        fallbackCredits: userData?.me?.quota?.credits
    });
    
    // Transcription mutation
    const [transcribeAudio] = useMutation(MUTATION.TRANSCRIBE_AUDIO, {
        onCompleted: (data) => {
            setIsTranscribing(false);
            if (data.transcribeAudio.__typename === 'TranscribeAudioError') {
                const errorMessage = data.transcribeAudio.message || lnT('messages:failedToTranscribeAudioPleaseTryAgain');
                const uiAction = mapTranscriptionErrorToUiAction({
                    error: data.transcribeAudio,
                    defaultDecision: transcriptionAccessDecision
                });
                if (uiAction.openUpgradeModal) {
                    openFeatureUpgradeModal({
                        preset: FEATURE_UPGRADE_MODAL_PRESET.AUDIO_TRANSCRIPTION,
                        upgradeType: 'personal'
                    });
                    setTranscriptionError(null);
                    return;
                }
                if (uiAction.openTeamUpgradeModal) {
                    openFeatureUpgradeModal({
                        preset: FEATURE_UPGRADE_MODAL_PRESET.AUDIO_TRANSCRIPTION,
                        upgradeType: 'team'
                    });
                    setTranscriptionError(null);
                    return;
                }
                if (data.transcribeAudio.code === 'INSUFFICIENT_CREDITS') {
                    setShowCreditsRequiredModal(true);
                    setTranscriptionError(null);
                    return;
                }
                setTranscriptionError(uiAction.showMessage || errorMessage);
            } else {
                const queuedStatus = getAudioProcessingStatusText(data.transcribeAudio?.metadata?.audioProcessing);
                setTranscriptionStatus(
                    queuedStatus || lnT('messages:transcriptionRunningInTheBackground')
                );
                setTranscriptionError(null);
                // The message will be updated via subscription, so no need to manually update
            }
        },
        onError: (error) => {
            setIsTranscribing(false);
            const errorMessage = getApolloErrorMessage(error, 'Failed to transcribe audio. Please try again.');
            if (errorMessage?.includes('Insufficient Credits')) {
                setShowCreditsRequiredModal(true);
                setTranscriptionError(null);
                return;
            }
            setTranscriptionError(errorMessage);
            console.error('Transcription error:', error);
        }
    });

    useEffect(() => {
        if (!isAudioProcessingActive(audioProcessing)) {
            setTranscriptionStatus(null);
        }
    }, [audioProcessing?.status, message?.body]);

    const [playing, setPlaying] = useState(false);
    const [hasStartedPlayback, setHasStartedPlayback] = useState(false);
    const [controls, setControls] = useState(false);
    const [light, setLight] = useState(false);
    const [volume, setVolume] = useState(0.8);
    const [muted, setMuted] = useState(false);
    const [played, setPlayed] = useState(0);
    const [loaded, setLoaded] = useState(0);
    const normalizedInitialDuration = normalizeDurationToSeconds(initialDuration, { unitHint: lnT('common:seconds') });
    const [duration, setDuration] = useState(normalizedInitialDuration ?? 0);
    const [playbackRate, setPlaybackRate] = useState(1.0);
    const [loop, setLoop] = useState(false);
    const [pip, setPip] = useState(false);
    const [seeking, setSeeking] = useState(false);

    const syncDurationFromInternalPlayer = React.useCallback(() => {
        const internalPlayer = playerRef.current?.getInternalPlayer?.();
        const nextDuration = resolvePreferredMediaDuration({
            storedDuration: normalizedInitialDuration,
            runtimeDuration: internalPlayer?.duration
        });

        if (nextDuration !== null) {
            setDuration(nextDuration);
            return true;
        }

        return false;
    }, [lnT, normalizedInitialDuration]);

    useEffect(() => {
        setPlaybackUrl(url);
        hasRetriedPlaybackUrlRef.current = false;
        isResettingAfterEndRef.current = false;
        setHasStartedPlayback(false);
        setShowTranscriptionOutputMenu(false);
        if (durationSyncTimeoutRef.current) {
            window.clearTimeout(durationSyncTimeoutRef.current);
            durationSyncTimeoutRef.current = null;
        }
    }, [url, fileName]);

    useEffect(() => {
        setDuration(normalizedInitialDuration ?? 0);
    }, [normalizedInitialDuration, url, fileName]);

    useEffect(() => {
        const listener = (previousId, currentId) => {
            if (currentId === transcriptionMenuKey) {
                setShowTranscriptionOutputMenu(true);
            } else if (previousId === transcriptionMenuKey) {
                setShowTranscriptionOutputMenu(false);
            }
        };

        const removeListener = addMenuListener(listener);
        if (getOpenMenuMessage() === transcriptionMenuKey) {
            setShowTranscriptionOutputMenu(true);
        }

        return removeListener;
    }, [transcriptionMenuKey]);

    useEffect(() => {
        if (!showTranscriptionOutputMenu) {
            return undefined;
        }

        const handleOutsideClick = event => {
            if (transcriptionMenuPopperElement?.contains(event.target)) {
                return;
            }
            if (transcriptionMenuReferenceElement?.contains(event.target)) {
                return;
            }
            setOpenMenuMessage(null);
        };

        const handleEscape = event => {
            if (event.key === 'Escape') {
                setOpenMenuMessage(null);
            }
        };

        document.addEventListener('click', handleOutsideClick);
        document.addEventListener('keydown', handleEscape, true);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape, true);
        };
    }, [showTranscriptionOutputMenu, transcriptionMenuPopperElement, transcriptionMenuReferenceElement]);

    useEffect(() => {
        let cancelled = false;
        let attempts = 0;

        const internalPlayer = playerRef.current?.getInternalPlayer?.();
        const scheduleRetry = () => {
            if (cancelled || attempts >= 12) {
                return;
            }
            attempts += 1;
            durationSyncTimeoutRef.current = window.setTimeout(() => {
                if (!cancelled && !syncDurationFromInternalPlayer()) {
                    scheduleRetry();
                }
            }, 250);
        };

        const handleMetadata = () => {
            syncDurationFromInternalPlayer();
        };

        if (internalPlayer?.addEventListener) {
            internalPlayer.addEventListener('loadedmetadata', handleMetadata);
            internalPlayer.addEventListener('durationchange', handleMetadata);
            internalPlayer.addEventListener('canplay', handleMetadata);
        }

        if (!syncDurationFromInternalPlayer() && normalizedInitialDuration === null) {
            scheduleRetry();
        }

        return () => {
            cancelled = true;
            if (durationSyncTimeoutRef.current) {
                window.clearTimeout(durationSyncTimeoutRef.current);
                durationSyncTimeoutRef.current = null;
            }
            if (internalPlayer?.removeEventListener) {
                internalPlayer.removeEventListener('loadedmetadata', handleMetadata);
                internalPlayer.removeEventListener('durationchange', handleMetadata);
                internalPlayer.removeEventListener('canplay', handleMetadata);
            }
        };
    }, [playbackUrl, fileName, syncDurationFromInternalPlayer]);

    const handlePlayPause = () => {
        if (seeking) {
            playerRef.current.seekTo(played);
            setSeeking(false);
        }
        if (!playing) {
            isResettingAfterEndRef.current = false;
        }
        setPlaying(!playing);
    };

    const handlePlay = () => {
        console.log('onPlay');
        isResettingAfterEndRef.current = false;
        setPlaying(true);
        setHasStartedPlayback(true);
    };

    const handlePause = () => {
        console.log('onPause');
        setPlaying(false);
    };

    const handleSeekMouseDown = e => {
        e.stopPropagation();
        setSeeking(true);
    };

    const handleSeekChange = e => {
        e.stopPropagation();
        setPlayed(parseFloat(e.target.value));
        playerRef.current.seekTo(parseFloat(e.target.value));
    };

    const handleSeekMouseUp = e => {
        e.stopPropagation();
        setSeeking(false);
    };

    const handleProgress = state => {
        if (isResettingAfterEndRef.current) {
            return;
        }
        // We only want to update time slider if we are not currently seeking
        if (!hasStartedPlayback && state.played > 0) {
            setHasStartedPlayback(true);
        }
        if (!seeking) {
            setLoaded(state.loaded);
            setPlayed(state.played);
        }
    };

    const handleEnded = () => {
        console.log('onEnded');
        setPlaying(loop);
        if (!loop) {
            isResettingAfterEndRef.current = true;
            if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
                playerRef.current.seekTo(0);
            }
            setPlayed(0);
            setLoaded(0);
            setHasStartedPlayback(false);
            setSeeking(false);
        }
    };

    const handleDuration = nextDuration => {
        const normalizedDuration = resolvePreferredMediaDuration({
            storedDuration: normalizedInitialDuration,
            runtimeDuration: nextDuration
        });
        if (normalizedDuration !== null) {
            setDuration(normalizedDuration);
        }
    };

    const handleOnReady = () => {
        syncDurationFromInternalPlayer();
    };

    const handleCyclePlaybackRate = e => {
        e.stopPropagation();
        const currentIndex = PLAYBACK_SPEEDS.findIndex(speed => speed === playbackRate);
        const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % PLAYBACK_SPEEDS.length : 0;
        setPlaybackRate(PLAYBACK_SPEEDS[nextIndex]);
    };

    const refreshPlaybackUrl = async () => {
        if (!fileName || isRefreshingPlaybackUrl) {
            return false;
        }

        setIsRefreshingPlaybackUrl(true);
        try {
            const { data, error } = await refetchDownloadFile({
                data: {
                    fileName
                }
            });

            if (error) {
                return false;
            }

            if (data?.downloadFile?.__typename === 'S3File' && data?.downloadFile?.url) {
                setPlaybackUrl(data.downloadFile.url);
                return true;
            }
            return false;
        } catch (refreshError) {
            return false;
        } finally {
            setIsRefreshingPlaybackUrl(false);
        }
    };

    const handlePlaybackError = async error => {
        if (fallbackUrl && playbackUrl !== fallbackUrl) {
            setPlaybackUrl(fallbackUrl);
            return;
        }

        if (!hasRetriedPlaybackUrlRef.current) {
            hasRetriedPlaybackUrlRef.current = true;
            const refreshed = await refreshPlaybackUrl();
            if (refreshed) {
                return;
            }
        }

        setPlaying(false);
        console.error('Audio playback failed', {
            messageId: message?.id,
            fileName,
            error: error?.message || error
        });
    };

    const preventDragHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const startTranscription = outputFormat => {
        if (requiresUpgradeForTranscription) {
            openFeatureUpgradeModal({
                preset: FEATURE_UPGRADE_MODAL_PRESET.AUDIO_TRANSCRIPTION,
                upgradeType: 'personal'
            });
            return;
        }
        if (requiresTeamUpgradeForTranscription) {
            openFeatureUpgradeModal({
                preset: FEATURE_UPGRADE_MODAL_PRESET.AUDIO_TRANSCRIPTION,
                upgradeType: 'team'
            });
            return;
        }

        if (!message || !message.id) {
            setTranscriptionError(lnT('messages:messageIdNotAvailable'));
            return;
        }

        if (credits <= 0) {
            setShowCreditsRequiredModal(true);
            return;
        }
        
        setIsTranscribing(true);
        setTranscriptionError(null);
        setTranscriptionStatus(null);
        setOpenMenuMessage(null);
        
        transcribeAudio({
            variables: {
                data: {
                    messageId: message.id,
                    outputFormat,
                    ...(outputFormat === AUDIO_TRANSCRIPTION_OUTPUT_FORMAT.TIMESTAMPED
                        ? { provider: 'openai' }
                        : {})
                }
            }
        });
    };

    const handleTranscribe = outputFormat => {
        if (showTranscriptionIntro && !getShouldHideTranscriptionIntro()) {
            setPendingOutputFormat(outputFormat);
            setHideTranscriptionIntro(false);
            setShowTranscriptionIntroModal(true);
            setOpenMenuMessage(null);
            return;
        }

        startTranscription(outputFormat);
    };

    const handleConfirmTranscriptionIntro = () => {
        if (hideTranscriptionIntro && typeof window !== 'undefined') {
            try {
                window.localStorage.setItem(TRANSCRIPTION_INTRO_DISMISS_KEY, 'true');
            } catch {
                // Ignore storage limitations
            }
        }

        const nextOutputFormat = pendingOutputFormat;
        setShowTranscriptionIntroModal(false);
        setPendingOutputFormat(null);
        setHideTranscriptionIntro(false);

        if (nextOutputFormat) {
            startTranscription(nextOutputFormat);
        }
    };

    const handleCloseTranscriptionIntroModal = () => {
        setShowTranscriptionIntroModal(false);
        setPendingOutputFormat(null);
        setHideTranscriptionIntro(false);
    };

    const handleToggleTranscriptionOutputMenu = () => {
        if (isTranscribing) {
            return;
        }

        if (requiresUpgradeForTranscription || requiresTeamUpgradeForTranscription) {
            openFeatureUpgradeModal({
                preset: FEATURE_UPGRADE_MODAL_PRESET.AUDIO_TRANSCRIPTION,
                upgradeType: requiresTeamUpgradeForTranscription ? 'team' : 'personal'
            });
            return;
        }

        setTranscriptionError(null);
        if (showTranscriptionOutputMenu) {
            setOpenMenuMessage(null);
            return;
        }

        const currentlyOpen = getOpenMenuMessage();
        if (currentlyOpen && currentlyOpen !== transcriptionMenuKey) {
            setOpenMenuMessage(null);
        }

        setOpenMenuMessage(transcriptionMenuKey);
    };

    const renderTranscriptionMenuIcon = optionValue => {
        if (optionValue === AUDIO_TRANSCRIPTION_OUTPUT_FORMAT.TIMESTAMPED) {
            return <FiClock size={14} />;
        }

        if (optionValue === AUDIO_TRANSCRIPTION_OUTPUT_FORMAT.SUMMARIZED) {
            return <LuScanText size={14} />;
        }

        return <FiFileText size={14} />;
    };

    const renderTranscriptionMenuPortal = () => {
        if (!showTranscriptionOutputMenu || !transcriptionMenuReferenceElement || typeof document === 'undefined') {
            return null;
        }

        return ReactDOM.createPortal(
            <DropDownMenu
                ref={setTranscriptionMenuPopperElement}
                style={transcriptionMenuPopperStyles.popper}
                {...transcriptionMenuPopperAttributes.popper}
                onClick={e => e.stopPropagation()}
            >
                <div role="menu" style={{ width: 220 }}>
                    <div role="content">
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                            {AUDIO_TRANSCRIPTION_VISIBLE_OPTIONS.map(option => (
                                <li key={option.value}>
                                    <Element
                                        onClick={e => {
                                            e.stopPropagation();
                                            handleTranscribe(option.value);
                                        }}
                                    >
                                        <MenuItemLabel>
                                            <MenuItemIcon>
                                                {renderTranscriptionMenuIcon(option.value)}
                                            </MenuItemIcon>
                                            <span style={{ fontSize: 13, lineHeight: 1.2 }}>
                                                {option.menuLabel || option.label}
                                            </span>
                                        </MenuItemLabel>
                                    </Element>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </DropDownMenu>,
            document.body
        );
    };

    return (
        <div 
            className="audio-player-container"
            style={{ pointerEvents: 'auto' }}
        >
            <div>
                <ReactPlayer
                    ref={playerRef}
                    width="0"
                    height="0"
                    url={playbackUrl}
                    playing={playing}
                    controls={controls}
                    light={light}
                    loop={loop}
                    playbackRate={playbackRate}
                    volume={volume}
                    muted={muted}
                    onReady={handleOnReady}
                    onStart={() => console.log('onStart')}
                    onPlay={handlePlay}
                    onPause={handlePause}
                    onBuffer={() => console.log('onBuffer')}
                    onSeek={e => console.log('onSeek', e)}
                    onEnded={handleEnded}
                    onProgress={handleProgress}
                    onDuration={handleDuration}
                    onError={handlePlaybackError}
                />
            </div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    width: '100%',
                    minWidth: 0
                }}
            >
                <div
                    style={{
                        borderRadius: 40,
                        height: 40,
                        width: 40,
                        backgroundColor: FILE_BLUE,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer'
                    }}
                    onClick={e => {
                        e.stopPropagation();
                        handlePlayPause();
                    }}
                >
                    {!playing ? (
                        <FaPlay style={{ height: 16, width: 16, color: WHITE, marginLeft: 2 }} />
                    ) : (
                        <IoIosPause style={{ height: 20, width: 20, color: WHITE }} />
                    )}
                </div>
                <div
                    style={{
                        flexDirection: 'column',
                        marginLeft: 12,
                        flex: 1,
                        width: '100%',
                        minWidth: 0
                    }}
                >
                    {hasStartedPlayback ? (
                        <input
                            className="audio-shared-scrubber"
                            style={{
                                marginTop: 0,
                                marginBottom: 8,
                                width: '100%',
                                minWidth: 0,
                                cursor: 'pointer',
                                pointerEvents: 'auto'
                            }}
                            type="range"
                            min={0}
                            max={0.999999}
                            step="any"
                            value={played}
                            onMouseDown={handleSeekMouseDown}
                            onChange={handleSeekChange}
                            onMouseUp={handleSeekMouseUp}
                            data-prevent-message-interaction="true"
                            draggable={false}
                        />
                    ) : null}
                    {showTitle && !hasStartedPlayback ? (
                        <div
                            style={{
                                width: '100%',
                                minWidth: 0,
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word'
                            }}
                        >
                            {decodeDisplayText(title)}
                        </div>
                    ) : null}
                    <div 
                        style={{
                            fontSize: 12,
                            marginTop: hasStartedPlayback ? 4 : showTitle ? 6 : 4,
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 0
                        }}
                    >
                        <div className="audio-time-row">
                            {hasStartedPlayback ? (
                                <>
                                    <Duration seconds={duration * played} /> / <Duration seconds={duration} />
                                </>
                            ) : (
                                <Duration seconds={duration} />
                            )}
                            {hasStartedPlayback ? (
                                <span className="audio-speed-slot">
                                    <button
                                        type="button"
                                        className="audio-shared-pill-button"
                                        onClick={handleCyclePlaybackRate}
                                        title={lnT('messages:playbackSpeed', { value1: String(formatPlaybackRate(playbackRate)) })}
                                    >
                                        {formatPlaybackRate(playbackRate)}
                                    </button>
                                </span>
                            ) : null}
                        </div>
                    </div>
                </div>
                {showTranscriptionAction ? (
                    <div
                        style={{
                            position: 'relative'
                        }}
                    >
                        <div
                            ref={setTranscriptionMenuReferenceElement}
                            onClick={e => {
                                e.stopPropagation();
                                handleToggleTranscriptionOutputMenu();
                            }}
                            className="audio-transcribe-pill-button"
                            style={{
                                cursor: isTranscribing ? 'not-allowed' : 'pointer',
                                opacity: isTranscribing ? 0.5 : 1
                            }}
                            title={
                                isTranscribing
                                    ? lnT('messages:transcribing_4e9a9366')
                                    : (requiresUpgradeForTranscription || requiresTeamUpgradeForTranscription)
                                        ? upgradeTooltip
                                        : lnT('messages:chooseTranscriptionFormat')
                            }
                        >
                            {isTranscribing ? (
                                <div className="audio-transcribe-pill-spinner" />
                            ) : (
                                <React.Fragment>
                                    <FiArrowRight size={12} color="currentColor" aria-hidden="true" />
                                    <span className="audio-transcribe-pill-label">{lnT('messages:a')}</span>
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
            {renderTranscriptionMenuPortal()}
            {transcriptionError ? (
                <div className="audio-transcription-status audio-transcription-status--error">
                    <span>{transcriptionError}</span>
                </div>
            ) : null}
            {!transcriptionError && (serverTranscriptionStatus || transcriptionStatus) ? (
                <div className={`audio-transcription-status audio-transcription-status--${transcriptionStatusTone}`}>
                    {transcriptionStatusTone === 'progress' ? <div className="audio-transcription-status-spinner" /> : null}
                    <span>{serverTranscriptionStatus || transcriptionStatus}</span>
                </div>
            ) : null}
            {isTranscribing ? (
                <div className="audio-transcription-status audio-transcription-status--progress">
                    <div className="audio-transcription-status-spinner" />
                    <span>{lnT('messages:transcriptionRunningInTheBackground')}</span>
                </div>
            ) : null}
            {isUploading ? (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginTop: 8
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            maxWidth: 160,
                            height: 4,
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            borderRadius: 2,
                            overflow: 'hidden'
                        }}
                    >
                        <div
                            style={{
                                height: '100%',
                                width: `${Math.min(uploadProgress, 100)}%`,
                                backgroundColor: uploadStatus === 'error' ? '#ff4444' : FILE_BLUE,
                                borderRadius: 2,
                                transition: 'width 0.15s ease-out'
                            }}
                        />
                    </div>
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
                        {uploadStatus === 'error'
                            ? lnT('messages:failed')
                            : uploadStatus === 'processing'
                                ? lnT('common:saving')
                                : Number(fileSize || 0) >= 2 * 1024 * 1024 * 1024
                                    ? lnT('common:text', { value1: String(Number(uploadProgress || 0).toFixed(1)) })
                                    : lnT('common:text', { value1: String(Math.round(Number(uploadProgress || 0))) })}
                    </span>
                    {typeof onCancelUpload === 'function' && uploadStatus !== 'error' ? (
                        <button
                            type="button"
                            title={lnT('messages:cancelUpload')}
                            aria-label={lnT('messages:cancelUpload')}
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                onCancelUpload();
                            }}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'rgba(255,255,255,0.8)',
                                borderRadius: 4,
                                opacity: 0.8,
                                transition: 'opacity 0.15s ease, color 0.15s ease'
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.opacity = '1';
                                e.currentTarget.style.color = '#ff4444';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.opacity = '0.8';
                                e.currentTarget.style.color = 'rgba(255,255,255,0.8)';
                            }}
                        >
                            <FiX size={12} />
                        </button>
                    ) : null}
                </div>
            ) : null}
            <CreditsRequiredModal
                isOpen={showCreditsRequiredModal}
                onRequestClose={() => setShowCreditsRequiredModal(false)}
                onCheckout={() => {
                    setShowCreditsRequiredModal(false);
                    openSettingsModal(SETTINGS_MODAL_TAB.UPGRADES);
                }}
            />
            <Modal
                isOpen={showTranscriptionIntroModal}
                onRequestClose={handleCloseTranscriptionIntroModal}
                style={{
                    overlay: {
                        backgroundColor: 'rgba(0, 0, 0, 0.55)',
                        zIndex: 10002
                    },
                    content: {
                        maxWidth: 420,
                        width: 'calc(100vw - 32px)',
                        inset: '50% auto auto 50%',
                        transform: 'translate(-50%, -50%)',
                        borderRadius: 16,
                        border: '1px solid rgba(255,255,255,0.12)',
                        background: '#12161d',
                        padding: 0,
                        overflow: 'hidden',
                        boxShadow: '0 24px 80px rgba(0,0,0,0.45)'
                    }
                }}
                contentLabel={lnT('messages:audioTranscriptionNotice')}
            >
                <div style={{ padding: 20 }}>
                    <div style={{ fontSize: 18, fontWeight: 600, color: 'rgba(255, 255, 255, 0.94)', marginBottom: 8 }}>{lnT('messages:transcribeThisAudio')}</div>
                    <p
                        style={{
                            margin: 0,
                            fontSize: 14,
                            lineHeight: 1.5,
                            color: 'rgba(255, 255, 255, 0.7)'
                        }}
                    >{lnT('messages:weLlProcessTheAudioInThe')}</p>
                    <label
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            marginTop: 16,
                            fontSize: 14,
                            color: 'rgba(255, 255, 255, 0.84)',
                            cursor: 'pointer'
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={hideTranscriptionIntro}
                            onChange={e => setHideTranscriptionIntro(e.target.checked)}
                        />{lnT('messages:donTShowThisAgain')}</label>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 10,
                            marginTop: 20
                        }}
                    >
                        <button
                            type="button"
                            onClick={handleCloseTranscriptionIntroModal}
                            style={{
                                border: '1px solid rgba(255, 255, 255, 0.14)',
                                background: 'rgba(255, 255, 255, 0.04)',
                                color: 'rgba(255, 255, 255, 0.84)',
                                borderRadius: 10,
                                padding: '10px 14px',
                                fontSize: 14,
                                cursor: 'pointer'
                            }}
                        >{lnT('common:cancel')}</button>
                        <button
                            type="button"
                            onClick={handleConfirmTranscriptionIntro}
                            style={{
                                border: '1px solid #598bff',
                                background: '#598bff',
                                color: '#fff',
                                borderRadius: 10,
                                padding: '10px 14px',
                                fontSize: 14,
                                fontWeight: 600,
                                cursor: 'pointer'
                            }}
                        >{lnT('common:continue')}</button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default AudioMessage;
